import { initializeApp, getApp, getApps } from 'firebase/app';
import { getMessaging, getToken, deleteToken, onMessage, isSupported as isMessagingSupported } from 'firebase/messaging';
import type { Messaging, MessagePayload } from 'firebase/messaging';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
// Remove the circular import
import type { Notification } from './notification-service';
import logger from '../shared/utils/logger';

/**
 * Firebase Cloud Messaging Service
 * 
 * Handles FCM token registration and message handling
 */
class FcmService {
  private messaging: Messaging | null = null;
  private fcmToken: string | null = null;
  private initialized: boolean = false;
  private callbacks: Set<(notification: Notification) => void> = new Set(); // Use a Set instead of an array
  // Add a token registration callback
  private tokenRegistrationCallback: ((token: string, deviceType: string) => Promise<void>) | null = null;

  /**
   * Set token registration callback
   * @param callback Function to call when registering token with backend
   */
  public setTokenRegistrationCallback(callback: (token: string, deviceType: string) => Promise<void>): void {
    this.tokenRegistrationCallback = callback;
  }

  /**
   * Check if FCM is supported on this platform
   * @returns Promise<boolean>
   */
  public async isSupported(): Promise<boolean> {
    try {
      // For native platforms, we check if push notifications are available
      if (Capacitor.isNativePlatform()) {
        return true; // We'll check permissions during initialization
      } else {
        // For web, check if browser supports Firebase messaging
        return await isMessagingSupported();
      }
    } catch (error: unknown) {
      logger.error('Error checking FCM support:', error);
      return false;
    }
  }

  /**
   * Initialize FCM service
   * @param userId User ID for token registration
   * @returns Promise<void>
   */
  public async initialize(userId: string): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      // Check if we're on a native platform
      if (Capacitor.isNativePlatform()) {
        await this.initializeNative(userId);
      } else {
        await this.initializeWeb(userId);
      }

      this.initialized = true;
    } catch (error: unknown) {
      throw error;
    }
  }

  /**
   * Initialize FCM for web
   * @param userId User ID for token registration
   * @returns Promise<void>
   */
  private async initializeWeb(userId: string): Promise<void> {
    await this.ensureWebMessaging();

    // Only fetch a token if the user has ALREADY granted notification permission.
    // getToken() calls Notification.requestPermission() internally, and browsers
    // reject that prompt unless it runs inside a short-running user-generated event
    // handler. Requesting it here (during automatic init on login) makes the browser
    // block the prompt and Firebase throw messaging/permission-blocked. The prompt is
    // instead triggered from a user gesture via enableWebPush().
    if (this.getWebNotificationPermission() === 'granted') {
      await this.requestAndRegisterToken(userId);
    } else {
      logger.info('Notification permission not granted yet; deferring FCM token until the user enables notifications');
    }
  }

  /**
   * Lazily initialize the Firebase app + messaging instance for web.
   * Idempotent and side-effect free with respect to permissions — it never
   * requests notification permission, so it is safe to call during auto-init.
   * @returns Promise<void>
   */
  private async ensureWebMessaging(): Promise<void> {
    if (this.messaging) {
      return;
    }

    const supported = await isMessagingSupported();
    if (!supported) {
      throw new Error('Firebase messaging is not supported in this browser');
    }

    const firebaseConfig = {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_FIREBASE_APP_ID
    };

    // Reuse the existing app instance if one was already created (e.g. via a
    // separate enableWebPush() call) to avoid Firebase's "app already exists" error.
    const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
    this.messaging = getMessaging(app);

    // Set up foreground message handler
    onMessage(this.messaging, (payload) => {
      this.handleFcmMessage(payload);
    });
  }

  /**
   * Current browser notification permission, or null when the API is unavailable.
   * @returns NotificationPermission | null
   */
  private getWebNotificationPermission(): NotificationPermission | null {
    if (typeof Notification === 'undefined') {
      return null;
    }
    return Notification.permission;
  }

  /**
   * Request browser notification permission and register an FCM token.
   *
   * MUST be invoked from within a user-gesture handler (e.g. a button onClick).
   * Browsers only allow Notification.requestPermission() inside a short-running,
   * user-generated event handler — which is exactly why this work cannot run during
   * automatic initialization on login.
   *
   * @param userId User ID for token registration
   * @returns Promise<boolean> true when a token was obtained
   */
  public async enableWebPush(userId: string): Promise<boolean> {
    // On native platforms permission is handled by Capacitor during initialize().
    if (Capacitor.isNativePlatform()) {
      await this.initialize(userId);
      return this.fcmToken !== null;
    }

    if (typeof Notification === 'undefined') {
      logger.warn('Notifications are not supported in this browser');
      return false;
    }

    try {
      await this.ensureWebMessaging();

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        logger.warn(`Notification permission "${permission}"; web push not enabled`);
        return false;
      }

      await this.requestAndRegisterToken(userId);
      return this.fcmToken !== null;
    } catch (error: unknown) {
      logger.error('Error enabling web push:', error);
      return false;
    }
  }

  /**
   * Initialize FCM for native platforms
   * @param userId User ID for token registration
   * @returns Promise<void>
   */
  private async initializeNative(userId: string): Promise<void> {
    try {
      // Request permission
      const permissionStatus = await PushNotifications.requestPermissions();
      
      if (permissionStatus.receive !== 'granted') {
        throw new Error('Push notification permission was denied');
      }
      
      // Register with native layer
      await PushNotifications.register();
      
      // Add listeners
      PushNotifications.addListener('registration', (token) => {
        this.fcmToken = token.value;
        this.registerTokenWithBackend(userId, token.value);
      });
      
      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        this.handleNativeNotification(notification);
      });
      
      PushNotifications.addListener('pushNotificationActionPerformed', (_notification) => {
        // action handled silently
      });
    } catch (error: unknown) {
      throw error;
    }
  }

  /**
   * Request FCM token and register with backend
   * @param userId User ID for token registration
   * @returns Promise<void>
   */
  private async requestAndRegisterToken(userId: string): Promise<void> {
    try {
      const token = await this.requestFCMToken();
      
      if (token) {
        this.fcmToken = token;
        await this.registerTokenWithBackend(userId, token);
      }
    } catch (error: unknown) {
      throw error;
    }
  }

  /**
   * Request FCM token from Firebase
   * @returns Promise<string | null>
   */
  private async requestFCMToken(): Promise<string | null> {
    try {
      if (!this.messaging) {
        logger.warn('Firebase messaging not initialized');
        return null;
      }
      const currentToken = await getToken(this.messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY
      });
      
      if (currentToken) {
        return currentToken;
      } else {
        logger.warn('No FCM token available');
        return null;
      }
    } catch (error: unknown) {
      logger.error('Error getting FCM token:', error);
      return null;
    }
  }

  /**
   * Register FCM token with backend
   * @param userId User ID for token registration
   * @param token FCM token
   * @returns Promise<void>
   */
  private async registerTokenWithBackend(_userId: string, token: string): Promise<void> {
    try {
      const deviceType = Capacitor.isNativePlatform() 
        ? Capacitor.getPlatform() 
        : 'web';
      
      // Use the callback instead of direct import
      if (this.tokenRegistrationCallback) {
        await this.tokenRegistrationCallback(token, deviceType);
        // token registered successfully
      } else {
        logger.warn('No token registration callback set');
      }
    } catch (error: unknown) {
      logger.error('Error registering FCM token with backend:', error);
    }
  }

  /**
   * Handle FCM message
   * @param payload FCM message payload
   */
  private handleFcmMessage(payload: MessagePayload): void {
    try {
      if (!payload || !payload.notification) {
        return;
      }
      
      const { notification, data } = payload;
      
      // Create notification object
      const notificationObj: Notification = {
        notificationId: Date.now(),
        employeeId: parseInt(data?.employeeId || '0'),
        type: data?.type || 'system',
        title: notification?.title ?? '',
        message: notification?.body ?? '',
        data: (data as Record<string, unknown>) || {},
        isRead: false,
        createdAt: new Date().toISOString()
      };
      
      // Notify callbacks
      this.notifyCallbacks(notificationObj);
    } catch (error: unknown) {
      logger.error('Error handling FCM message:', error);
    }
  }

  /**
   * Handle native notification
   * @param notification Native notification from Capacitor PushNotifications
   */
  private handleNativeNotification(notification: { title?: string; body?: string; data?: Record<string, string> }): void {
    try {
      // Create notification object
      const notificationObj: Notification = {
        notificationId: Date.now(),
        employeeId: parseInt(notification.data?.employeeId || '0'),
        type: notification.data?.type || 'system',
        title: notification.title ?? '',
        message: notification.body ?? '',
        data: (notification.data as Record<string, unknown>) || {},
        isRead: false,
        createdAt: new Date().toISOString()
      };
      
      // Notify callbacks
      this.notifyCallbacks(notificationObj);
    } catch (error: unknown) {
      logger.error('Error handling native notification:', error);
    }
  }

  /**
   * Register notification callback
   * @param callback Notification callback function
   */
  public registerNotificationCallback(callback: (notification: Notification) => void): void {
    this.callbacks.add(callback);
  }

  /**
   * Unregister notification callback
   * @param callback Notification callback function
   */
  public unregisterNotificationCallback(callback: (notification: Notification) => void): void {
    this.callbacks.delete(callback);
  }

  /**
   * Notify all callbacks
   * @param notification Notification object
   */
  private notifyCallbacks(notification: Notification): void {
    this.callbacks.forEach(callback => {
      try {
        callback(notification);
      } catch (error: unknown) {
        logger.error('Error in notification callback:', error);
      }
    });
  }

  /**
   * Get FCM token
   * @returns string | null
   */
  public getFCMToken(): string | null {
    return this.fcmToken;
  }

  /**
   * Cleanup FCM service
   *
   * Drops the cached token and messaging instance too, so a later login cannot reuse
   * a token that belonged to the previous session.
   */
  public cleanup(): void {
    this.callbacks.clear();
    if (Capacitor.isNativePlatform()) {
      PushNotifications.removeAllListeners();
    }
    this.fcmToken = null;
    this.messaging = null;
    this.initialized = false;
  }

  /**
   * Fully tear down push notifications for this device.
   *
   * Web: deleteToken() invalidates the token at FCM itself, so Google stops delivering
   * to this browser AND the backend's next send for that token comes back UNREGISTERED,
   * which prunes the stale row server-side. That second effect is what covers the paths
   * where we cannot call the backend — an expired session, or a hard 401 redirect.
   *
   * Native: PushNotifications.unregister() releases the device registration.
   *
   * Never throws: logout must complete whether or not any of this succeeds.
   *
   * @returns Promise<void>
   */
  public async teardown(): Promise<void> {
    if (!Capacitor.isNativePlatform() && this.messaging && this.fcmToken) {
      try {
        await deleteToken(this.messaging);
      } catch (error: unknown) {
        logger.warn('Failed to delete FCM token during teardown:', error);
      }
    }

    if (Capacitor.isNativePlatform()) {
      try {
        await PushNotifications.unregister();
      } catch (error: unknown) {
        logger.warn('Failed to unregister native push during teardown:', error);
      }
    }

    this.cleanup();
  }
}

// Create and export singleton instance
const fcmService = new FcmService();
export default fcmService; 